import { useRef, useEffect, useCallback } from 'react';
import type { Problem } from '../../types';
import type { SolutionMove } from '../../store/gameStore';
import { sgfToColRow, getBoundingBox } from '../../utils/coords';
import './Board.css';

interface BoardProps {
  problem: Problem;
  onMove: (coord: string) => void;
  disabled?: boolean;
  opponentMove?: string;
  hintArea?: { minCol: number; maxCol: number; minRow: number; maxRow: number } | null;
  hintCoord?: string | null;
  solutionPath?: SolutionMove[] | null;
}

const LINE_COLOR = '#5A3E1B';
const HINT_COLOR = 'rgba(59, 130, 246, 0.3)';
const HINT_EXACT_COLOR = 'rgba(59, 130, 246, 0.7)';

const STAR_POINTS = [
  [3, 3], [3, 9], [3, 15],
  [9, 3], [9, 9], [9, 15],
  [15, 3], [15, 9], [15, 15],
];

function drawWoodGrain(ctx: CanvasRenderingContext2D, w: number, h: number) {
  ctx.fillStyle = '#DCB35C';
  ctx.fillRect(0, 0, w, h);

  // Procedural wood grain lines
  const seed = 42;
  for (let i = 0; i < 50; i++) {
    const baseY = (i / 50) * h;
    const r = ((seed * (i + 1) * 7) % 30) - 15;
    const shade = ((seed * (i + 1) * 13) % 3);
    const colors = ['#C9A24E', '#D4AD55', '#E5C06A'];
    const alpha = 0.05 + ((seed * (i + 1) * 11) % 10) / 100;

    ctx.strokeStyle = colors[shade];
    ctx.globalAlpha = alpha;
    ctx.lineWidth = 1 + ((seed * (i + 1) * 3) % 2);
    ctx.beginPath();
    for (let x = 0; x <= w; x += 4) {
      const y = baseY + Math.sin((x + r * 10) / (60 + r)) * (3 + Math.abs(r) * 0.3);
      if (x === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

function drawBlackStone(ctx: CanvasRenderingContext2D, x: number, y: number, r: number) {
  // Shadow
  const shadow = ctx.createRadialGradient(x + 2, y + 2, r * 0.3, x + 2, y + 2, r * 1.1);
  shadow.addColorStop(0, 'rgba(0,0,0,0.3)');
  shadow.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = shadow;
  ctx.beginPath();
  ctx.arc(x + 2, y + 2, r * 1.1, 0, Math.PI * 2);
  ctx.fill();

  // Body: 3-stop gradient
  const body = ctx.createRadialGradient(x - r * 0.3, y - r * 0.3, r * 0.05, x, y, r);
  body.addColorStop(0, '#606060');
  body.addColorStop(0.5, '#2a2a2a');
  body.addColorStop(1, '#0a0a0a');
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();

  // Edge rim
  ctx.strokeStyle = 'rgba(0,0,0,0.3)';
  ctx.lineWidth = 0.5;
  ctx.stroke();

  // Specular highlight
  const spec = ctx.createRadialGradient(x - r * 0.35, y - r * 0.35, 0, x - r * 0.25, y - r * 0.25, r * 0.4);
  spec.addColorStop(0, 'rgba(255,255,255,0.4)');
  spec.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = spec;
  ctx.beginPath();
  ctx.arc(x - r * 0.25, y - r * 0.25, r * 0.4, 0, Math.PI * 2);
  ctx.fill();
}

function drawWhiteStone(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, col: number, row: number) {
  // Shadow
  const shadow = ctx.createRadialGradient(x + 2, y + 2, r * 0.3, x + 2, y + 2, r * 1.1);
  shadow.addColorStop(0, 'rgba(0,0,0,0.25)');
  shadow.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = shadow;
  ctx.beginPath();
  ctx.arc(x + 2, y + 2, r * 1.1, 0, Math.PI * 2);
  ctx.fill();

  // Body: 3-stop gradient
  const body = ctx.createRadialGradient(x - r * 0.3, y - r * 0.3, r * 0.05, x, y, r);
  body.addColorStop(0, '#ffffff');
  body.addColorStop(0.5, '#f5f0e8');
  body.addColorStop(1, '#d4cfc4');
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();

  // Shell lines (seeded by position)
  ctx.save();
  ctx.clip();
  const shellSeed = col * 19 + row;
  for (let i = 0; i < 4; i++) {
    const angle = ((shellSeed * 37 + i * 53) % 180) * Math.PI / 180;
    const offset = ((shellSeed * 23 + i * 17) % 10) - 5;
    ctx.strokeStyle = 'rgba(180,170,150,0.15)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    const cx = x + offset * 0.5;
    const cy = y + offset * 0.5;
    ctx.moveTo(cx - r * Math.cos(angle), cy - r * Math.sin(angle));
    ctx.quadraticCurveTo(
      cx + offset * 2, cy + offset * 2,
      cx + r * Math.cos(angle), cy + r * Math.sin(angle),
    );
    ctx.stroke();
  }
  ctx.restore();

  // Edge
  ctx.strokeStyle = '#b0a890';
  ctx.lineWidth = 0.5;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.stroke();

  // Specular highlight
  const spec = ctx.createRadialGradient(x - r * 0.35, y - r * 0.35, 0, x - r * 0.25, y - r * 0.25, r * 0.35);
  spec.addColorStop(0, 'rgba(255,255,255,0.6)');
  spec.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = spec;
  ctx.beginPath();
  ctx.arc(x - r * 0.25, y - r * 0.25, r * 0.35, 0, Math.PI * 2);
  ctx.fill();
}

export function Board({ problem, onMove, disabled, opponentMove, hintArea, hintCoord, solutionPath }: BoardProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Compute viewport (auto-crop)
  const allCoords = [...problem.black, ...problem.white];
  const viewport = getBoundingBox(allCoords, 2);
  const vpCols = viewport.maxCol - viewport.minCol + 1;
  const vpRows = viewport.maxRow - viewport.minRow + 1;
  const vpSize = Math.max(vpCols, vpRows, 7);

  const centerCol = (viewport.minCol + viewport.maxCol) / 2;
  const centerRow = (viewport.minRow + viewport.maxRow) / 2;
  const halfSize = vpSize / 2;
  const vp = {
    minCol: Math.max(0, Math.round(centerCol - halfSize)),
    maxCol: Math.min(18, Math.round(centerCol + halfSize)),
    minRow: Math.max(0, Math.round(centerRow - halfSize)),
    maxRow: Math.min(18, Math.round(centerRow + halfSize)),
  };

  const draw = useCallback((canvas: HTMLCanvasElement) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const w = canvas.width / window.devicePixelRatio;
    const h = canvas.height / window.devicePixelRatio;
    ctx.save();

    const cols = vp.maxCol - vp.minCol + 1;
    const rows = vp.maxRow - vp.minRow + 1;
    const cellSize = Math.min(w, h) / (Math.max(cols, rows) + 1);
    const marginX = (w - cellSize * (cols - 1)) / 2;
    const marginY = (h - cellSize * (rows - 1)) / 2;

    // Wood grain background
    drawWoodGrain(ctx, w, h);

    // Board frame
    ctx.strokeStyle = '#6B4226';
    ctx.lineWidth = 3;
    ctx.strokeRect(
      marginX - cellSize * 0.5,
      marginY - cellSize * 0.5,
      cellSize * (cols - 1) + cellSize,
      cellSize * (rows - 1) + cellSize,
    );

    // Grid lines
    ctx.strokeStyle = LINE_COLOR;
    ctx.lineWidth = Math.max(1, cellSize * 0.03);

    for (let i = 0; i < cols; i++) {
      const x = marginX + i * cellSize;
      ctx.beginPath();
      ctx.moveTo(x, marginY);
      ctx.lineTo(x, marginY + (rows - 1) * cellSize);
      ctx.stroke();
    }
    for (let j = 0; j < rows; j++) {
      const y = marginY + j * cellSize;
      ctx.beginPath();
      ctx.moveTo(marginX, y);
      ctx.lineTo(marginX + (cols - 1) * cellSize, y);
      ctx.stroke();
    }

    // Star points
    for (const [sc, sr] of STAR_POINTS) {
      if (sc >= vp.minCol && sc <= vp.maxCol && sr >= vp.minRow && sr <= vp.maxRow) {
        const x = marginX + (sc - vp.minCol) * cellSize;
        const y = marginY + (sr - vp.minRow) * cellSize;
        ctx.fillStyle = LINE_COLOR;
        ctx.beginPath();
        ctx.arc(x, y, cellSize * 0.12, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Hint area
    if (hintArea) {
      ctx.fillStyle = HINT_COLOR;
      const hx = marginX + (hintArea.minCol - vp.minCol) * cellSize - cellSize / 2;
      const hy = marginY + (hintArea.minRow - vp.minRow) * cellSize - cellSize / 2;
      const hw = (hintArea.maxCol - hintArea.minCol + 1) * cellSize;
      const hh = (hintArea.maxRow - hintArea.minRow + 1) * cellSize;
      ctx.fillRect(hx, hy, hw, hh);
    }

    // Hint exact coord
    if (hintCoord) {
      const [hc, hr] = sgfToColRow(hintCoord);
      if (hc >= vp.minCol && hc <= vp.maxCol && hr >= vp.minRow && hr <= vp.maxRow) {
        const x = marginX + (hc - vp.minCol) * cellSize;
        const y = marginY + (hr - vp.minRow) * cellSize;
        ctx.fillStyle = HINT_EXACT_COLOR;
        ctx.beginPath();
        ctx.arc(x, y, cellSize * 0.35, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Draw stones
    const stoneR = cellSize * 0.45;

    const drawStone = (sgfCoord: string, color: 'black' | 'white') => {
      const [col, row] = sgfToColRow(sgfCoord);
      if (col < vp.minCol || col > vp.maxCol || row < vp.minRow || row > vp.maxRow) return;

      const x = marginX + (col - vp.minCol) * cellSize;
      const y = marginY + (row - vp.minRow) * cellSize;

      if (color === 'black') {
        drawBlackStone(ctx, x, y, stoneR);
      } else {
        drawWhiteStone(ctx, x, y, stoneR, col, row);
      }
    };

    problem.black.forEach(c => drawStone(c, 'black'));
    problem.white.forEach(c => drawStone(c, 'white'));

    // Opponent move with last-move marker (colored dot)
    if (opponentMove) {
      drawStone(opponentMove, 'white');
      const [oc, or_] = sgfToColRow(opponentMove);
      if (oc >= vp.minCol && oc <= vp.maxCol && or_ >= vp.minRow && or_ <= vp.maxRow) {
        const x = marginX + (oc - vp.minCol) * cellSize;
        const y = marginY + (or_ - vp.minRow) * cellSize;
        // Red dot marker
        ctx.fillStyle = '#e53e3e';
        ctx.beginPath();
        ctx.arc(x, y, cellSize * 0.12, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Solution path: semi-transparent stones with move numbers
    if (solutionPath && solutionPath.length > 0) {
      for (const move of solutionPath) {
        const [col, row] = sgfToColRow(move.coord);
        if (col < vp.minCol || col > vp.maxCol || row < vp.minRow || row > vp.maxRow) continue;

        const x = marginX + (col - vp.minCol) * cellSize;
        const y = marginY + (row - vp.minRow) * cellSize;

        ctx.globalAlpha = 0.6;
        if (move.player === 'B') {
          drawBlackStone(ctx, x, y, stoneR);
        } else {
          drawWhiteStone(ctx, x, y, stoneR, col, row);
        }
        ctx.globalAlpha = 1;

        // Move number
        const numColor = move.player === 'B' ? '#ffffff' : '#1a1a1a';
        ctx.fillStyle = numColor;
        ctx.font = `bold ${cellSize * 0.38}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(String(move.moveNumber), x, y);
      }
    }

    ctx.restore();
  }, [problem, vp, opponentMove, hintArea, hintCoord, solutionPath]);

  // Resize and draw
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const resize = () => {
      const rect = container.getBoundingClientRect();
      const size = Math.min(rect.width, rect.height);
      canvas.width = size * window.devicePixelRatio;
      canvas.height = size * window.devicePixelRatio;
      canvas.style.width = `${size}px`;
      canvas.style.height = `${size}px`;
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
      draw(canvas);
    };

    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(container);
    return () => observer.disconnect();
  }, [draw]);

  // Click handling
  const handleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (disabled) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / window.devicePixelRatio / rect.width;
    const scaleY = canvas.height / window.devicePixelRatio / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;

    const cols = vp.maxCol - vp.minCol + 1;
    const rows = vp.maxRow - vp.minRow + 1;
    const w = canvas.width / window.devicePixelRatio;
    const h = canvas.height / window.devicePixelRatio;
    const cellSize = Math.min(w, h) / (Math.max(cols, rows) + 1);
    const marginX = (w - cellSize * (cols - 1)) / 2;
    const marginY = (h - cellSize * (rows - 1)) / 2;

    const col = Math.round((x - marginX) / cellSize) + vp.minCol;
    const row = Math.round((y - marginY) / cellSize) + vp.minRow;

    if (col < 0 || col > 18 || row < 0 || row > 18) return;

    const sgf = String.fromCharCode(97 + col) + String.fromCharCode(97 + row);
    onMove(sgf);
  }, [disabled, onMove, vp]);

  return (
    <div ref={containerRef} className="board-container">
      <canvas ref={canvasRef} onClick={handleClick} className="board-canvas" />
    </div>
  );
}
